export type ApiId = string;

// ===== Identity =====
export interface TokenPair {
  access: string;
  refresh: string;
  audience: 'customer' | 'internal';
}

export interface CustomerLoginPayload {
  identifier: string;
  password: string;
}

export interface RegisterPayload {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface ApiMessageResponse {
  detail: string;
}

export interface HandoffExchangeResponse {
  accessToken: string;
  refreshToken: string;
  next: string | null;
  tenantSlug: string | null;
  ssoTargets: Record<'pryspace' | 'pryops', boolean>;
}

export interface CustomerProfile {
  id: ApiId;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verified: boolean;
  time_created: number;
  time_updated: number;
}

// ===== Tenant access =====
export interface TenantGroupSummary {
  id: ApiId;
  name: string;
}

export interface CustomerTenant {
  id: ApiId;
  slug: string;
  name: string;
  is_active: boolean;
  time_created: number;
  time_updated: number;
  membership_id: ApiId;
  groups: TenantGroupSummary[];
  permissions: string[];
}

export interface TenantMe {
  tenant: Pick<CustomerTenant, 'id' | 'slug' | 'name' | 'is_active'> & {
    time_created: number;
    time_updated: number;
  };
  member: TenantMember | null;
  permissions: string[];
}

export interface TenantMember {
  id: ApiId;
  tenant: ApiId;
  user: ApiId;
  username: string;
  email: string;
  is_active: boolean;
  groups: TenantGroupSummary[];
  permissions: string[];
  time_created: number;
  time_updated: number;
}

export interface TenantUserLookup {
  id: ApiId;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  email_verified: boolean;
}

export interface TenantMemberAttach {
  user_id: ApiId;
  group_ids?: ApiId[];
}

export interface AccessChangedEvent {
  type: 'access.ready' | 'access.changed';
  event_id?: string;
  tenant_id: ApiId | null;
  user_id: ApiId | null;
  reason?: string | null;
}

export interface TenantGroup {
  id: ApiId;
  tenant: ApiId;
  name: string;
  is_system: boolean;
  permissions: string[];
  time_created: number;
  time_updated: number;
}

export interface TenantPermission {
  id: ApiId;
  codename: string;
  label: string;
  description: string;
  is_active: boolean;
}

export interface TenantPermissionOverride {
  id: ApiId;
  tenant: ApiId;
  permission: ApiId;
  target_member: ApiId | null;
  target_group: ApiId | null;
  mode: 'grant' | 'deny';
  reason: string;
  created_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface TenantMemberPatch {
  is_active?: boolean;
  group_ids?: ApiId[];
}

export interface TenantGroupCreate {
  name: string;
  permission_codes?: string[];
}

export interface TenantGroupPatch {
  name?: string;
  permission_codes?: string[];
}

export interface TenantPermissionOverrideCreate {
  permission: ApiId;
  target_member?: ApiId;
  target_group?: ApiId;
  mode: 'grant' | 'deny';
  reason?: string;
}

// ===== Merch =====
export type MerchProviderKey = 'printful' | 'yoycol' | string;
export type MerchStatus = 'draft' | 'active' | 'error' | 'disabled';
export type MerchDiscoveryStatus =
  | 'draft'
  | 'running'
  | 'completed'
  | 'error'
  | 'disabled';
export type MerchLifecycle =
  | 'candidate'
  | 'sample_planned'
  | 'ordered'
  | 'tested'
  | 'approved'
  | 'rejected';

export interface MerchProviderCredentialField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
}

export interface MerchProviderDefinition {
  id: ApiId;
  key: MerchProviderKey;
  display_name: string;
  adapter_key: string;
  capabilities: {
    credential_fields?: MerchProviderCredentialField[];
    [key: string]: unknown;
  };
  is_active: boolean;
}

export interface MerchProviderConnection {
  id: ApiId;
  tenant: ApiId;
  provider: MerchProviderKey;
  name: string;
  external_account_id: string;
  configuration: Record<string, unknown>;
  status: MerchStatus;
  has_credentials: boolean;
  credentials_updated_at: string | null;
  last_sync_at: string | null;
  last_error: string;
  time_created: number;
  time_updated: number;
}

export interface MerchProviderConnectionWrite {
  provider: MerchProviderKey;
  name: string;
  credential_ref?: string;
  credentials?: Record<string, string>;
  clear_credentials?: boolean;
  external_account_id?: string;
  configuration?: Record<string, unknown>;
  status?: 'draft' | 'disabled';
}

export interface MerchVariant {
  id: ApiId;
  external_variant_id: string;
  sku: string;
  title: string;
  size: string;
  color: string;
  color_code: string;
  currency: string;
  cost_amount: string | null;
  is_available: boolean;
  time_created: number;
  time_updated: number;
}

export interface MerchEvaluation {
  id: ApiId;
  product: ApiId;
  sample_status: 'not_planned' | 'planned' | 'ordered' | 'received';
  sample_ordered_at: string | null;
  sample_received_at: string | null;
  material_score: number | null;
  fit_score: number | null;
  fabric_weight_score: number | null;
  print_quality_score: number | null;
  workmanship_score: number | null;
  value_score: number | null;
  delivery_score: number | null;
  overall_score: number | null;
  notes: string;
  decision_note: string;
  updated_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchProduct {
  id: ApiId;
  tenant: ApiId;
  connection: ApiId;
  provider: {
    id: ApiId;
    provider: MerchProviderKey;
    name: string;
    status: MerchStatus;
  };
  external_product_id: string;
  external_sku: string;
  title: string;
  description: string;
  brand: string;
  model_name: string;
  category: string;
  print_method: string;
  material: string;
  gsm: number | null;
  product_url: string;
  thumbnail_url: string;
  currency: string;
  cost_amount: string | null;
  lifecycle: MerchLifecycle;
  is_in_catalog: boolean;
  variants: MerchVariant[];
  evaluation: MerchEvaluation | null;
  time_created: number;
  time_updated: number;
}

export interface MerchDiscoverySession {
  id: ApiId;
  tenant: ApiId;
  provider: MerchProviderKey;
  name: string;
  configuration: Record<string, unknown>;
  status: MerchDiscoveryStatus;
  has_credentials: boolean;
  last_probe_at: string | null;
  last_error: string;
  created_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchDiscoverySessionWrite {
  provider: MerchProviderKey;
  name: string;
  credential_ref?: string;
  configuration?: Record<string, unknown>;
}

export interface MerchDiscoveryProduct {
  id: ApiId;
  session: ApiId;
  provider: { key: MerchProviderKey; name: string };
  connection_id: ApiId | null;
  external_product_id: string;
  external_sku: string;
  title: string;
  description: string;
  brand: string;
  model_name: string;
  category: string;
  print_method: string;
  material: string;
  gsm: number | null;
  product_url: string;
  thumbnail_url: string;
  currency: string;
  cost_amount: string | null;
  variants: unknown[];
  lifecycle: string;
  is_selected: boolean;
  material_score: number | null;
  fit_score: number | null;
  fabric_weight_score: number | null;
  print_quality_score: number | null;
  workmanship_score: number | null;
  value_score: number | null;
  delivery_score: number | null;
  overall_score: number | null;
  notes: string;
  decision_note: string;
  updated_by: ApiId | null;
  promoted_product: ApiId | null;
  promoted_at: string | null;
  time_created: number;
  time_updated: number;
}

export interface MerchProviderCatalogPage {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  provider_counts: Partial<Record<MerchProviderKey, number>>;
  categories: string[];
  results: MerchDiscoveryProduct[];
}

export interface MerchProviderCatalogSyncResult {
  connection: ApiId;
  session: ApiId;
  provider: MerchProviderKey;
  products: number;
  created_products: number;
  updated_products: number;
  removed_products: number;
}

export interface MerchProbeRequest {
  provider: MerchProviderKey;
  credential_ref?: string;
  configuration?: Record<string, unknown>;
  discover?: boolean;
}

export interface MerchProbeResult {
  provider: MerchProviderKey;
  connection?: ApiId;
  adapter?: string;
  connected?: boolean;
  discovered?: boolean;
  product_count?: number;
  products?: unknown[];
  category_count?: number | null;
  catalog_pages?: number | null;
  error?: string | null;
}

export interface MerchProviderSyncResult {
  connection: ApiId;
  provider: MerchProviderKey;
  products: number;
  created_products: number;
  updated_products: number;
  variants: number;
}

export interface MerchEvaluationPatch {
  sample_status?: MerchEvaluation['sample_status'];
  sample_ordered_at?: string | null;
  sample_received_at?: string | null;
  material_score?: number | null;
  fit_score?: number | null;
  fabric_weight_score?: number | null;
  print_quality_score?: number | null;
  workmanship_score?: number | null;
  value_score?: number | null;
  delivery_score?: number | null;
  notes?: string;
  decision_note?: string;
}

export type MerchProductIdeaStatus = 'draft' | 'testing' | 'approved' | 'archived';
export type MerchBrandKind = 'owned' | 'partner';
export type MerchBrandStatus = 'active' | 'archived';
export type MerchStoreListingStatus = 'draft' | 'live';
export type MerchProviderTemplateValidationReason =
  | 'template_not_available'
  | 'connection_not_active'
  | 'connection_credentials_unavailable'
  | 'connection_resync_required'
  | 'provider_not_active'
  | 'template_not_ready'
  | 'external_product_required'
  | 'available_variant_required'
  | 'variant_sku_required'
  | 'design_code_required'
  | 'placement_required'
  | 'mockup_required';
export type MerchProductionReadinessReason =
  | MerchProviderTemplateValidationReason
  | 'production_template_required'
  | 'production_template_tenant_mismatch'
  | 'production_template_source_mismatch';
export type MerchSampleStatus =
  | 'planned'
  | 'ordered'
  | 'received'
  | 'tested'
  | 'approved'
  | 'rejected';
export type MerchDesignResult = 'untested' | 'optimal' | 'needs_changes' | 'rejected';
export type MerchDesignScale = 'unknown' | 'too_small' | 'optimal' | 'too_large';

export interface MerchProductIdeaFile {
  id: ApiId;
  idea: ApiId;
  original_name: string;
  content_type: string;
  file_size: number;
  is_mockup: boolean;
  is_active: boolean;
  content_url: string;
  created_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchBrand {
  id: ApiId;
  name: string;
  slug: string;
  kind: MerchBrandKind;
  status: MerchBrandStatus;
  agreement_summary: string;
  time_created: number;
  time_updated: number;
}

export interface MerchBrandWrite {
  name: string;
  kind: MerchBrandKind;
  status?: MerchBrandStatus;
  agreement_summary?: string;
}

export interface MerchProviderTemplateMockup {
  id: ApiId;
  url: string;
  variant_external_id: string;
  position: number;
  label: string;
}

export interface MerchProviderTemplateVariant {
  id: ApiId;
  external_variant_id: string;
  sku: string;
  title: string;
  size: string;
  color: string;
  color_code: string;
  currency: string;
  cost_amount: string | null;
  is_available: boolean;
}

export interface MerchProviderTemplate {
  id: ApiId;
  connection_id: ApiId;
  provider: {
    key: MerchProviderKey;
    name: string;
  };
  external_template_id: string;
  external_design_code: string;
  external_product_id: string;
  title: string;
  provider_status: string;
  is_available: boolean;
  template_ready: boolean;
  validation_reasons: MerchProviderTemplateValidationReason[];
  assignable?: boolean;
  assignment_issues?: MerchProductionReadinessReason[];
  variants: MerchProviderTemplateVariant[];
  mockups: MerchProviderTemplateMockup[];
  last_seen_at: string | null;
  time_created: number;
  time_updated: number;
}

export interface MerchProviderTemplateSyncResult {
  connection: ApiId;
  provider: MerchProviderKey;
  templates: number;
  created_templates: number;
  updated_templates: number;
  unavailable_templates: number;
  variants: number;
  mockups: number;
  drafted_listings: number;
}

export interface MerchProductIdea {
  id: ApiId;
  tenant: ApiId;
  name: string;
  description: string;
  brand: MerchBrand | null;
  provider_template: MerchProviderTemplate | null;
  production_ready: boolean;
  production_readiness_reasons: MerchProductionReadinessReason[];
  status: MerchProductIdeaStatus;
  files: MerchProductIdeaFile[];
  sample_count: number;
  source_count: number;
  created_by: ApiId | null;
  updated_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchProductIdeaWrite {
  name: string;
  description?: string;
  brand_id?: ApiId | null;
  status?: MerchProductIdeaStatus;
}

export type MerchStoreListingValidationReason =
  | 'brand_required'
  | 'brand_must_be_active'
  | 'price_must_be_positive'
  | 'public_media_required'
  | 'public_media_must_be_active'
  | 'active_primary_source_required'
  | 'production_template_required'
  | 'production_template_not_ready'
  | 'production_template_source_mismatch';

export interface MerchStoreListingPrimarySource {
  id: ApiId;
  provider: MerchProviderKey;
  product_id: ApiId;
  product_title: string;
}

export interface MerchStoreListing {
  id: ApiId;
  idea: { id: ApiId; name: string };
  brand: MerchBrand | null;
  provider_template: MerchProviderTemplate | null;
  production_ready: boolean;
  production_readiness_reasons: MerchProductionReadinessReason[];
  title: string;
  slug: string;
  price_amount: string | null;
  currency: string;
  status: MerchStoreListingStatus;
  media: MerchProductIdeaFile[];
  validation_reasons: MerchStoreListingValidationReason[];
  seller_name: string;
  primary_source: MerchStoreListingPrimarySource | null;
  published_at: string | null;
  time_created: number;
  time_updated: number;
}

export interface MerchStoreListingWrite {
  title: string;
  slug?: string;
  price_amount: string | null;
  currency: string;
  brand_id?: ApiId | null;
  media_ids: ApiId[];
}

export interface MerchSample {
  id: ApiId;
  idea: { id: ApiId; name: string; status: MerchProductIdeaStatus };
  product: MerchProduct;
  variant: MerchVariant | null;
  files: MerchProductIdeaFile[];
  status: MerchSampleStatus;
  provider_order_id: string;
  ordered_at: string | null;
  received_at: string | null;
  design_result: MerchDesignResult;
  design_scale: MerchDesignScale;
  design_score: number | null;
  print_quality_score: number | null;
  design_notes: string;
  decision_note: string;
  created_by: ApiId | null;
  updated_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchSamplePatch {
  variant_id?: ApiId | null;
  file_ids?: ApiId[];
  status?: MerchSampleStatus;
  provider_order_id?: string;
  ordered_at?: string | null;
  received_at?: string | null;
  design_result?: MerchDesignResult;
  design_scale?: MerchDesignScale;
  design_score?: number | null;
  print_quality_score?: number | null;
  design_notes?: string;
  decision_note?: string;
}

export interface MerchProductSource {
  id: ApiId;
  idea: { id: ApiId; name: string; status: MerchProductIdeaStatus };
  product: MerchProduct;
  approved_sample: ApiId | null;
  kind: 'primary' | 'alternative';
  is_active: boolean;
  notes: string;
  created_by: ApiId | null;
  updated_by: ApiId | null;
  time_created: number;
  time_updated: number;
}

export interface MerchProductSourcePatch {
  kind?: MerchProductSource['kind'];
  notes?: string;
  is_active?: boolean;
}

export type Paginated<T> = { results: T[]; count?: number } | T[];

export function unwrapList<T>(payload: Paginated<T>): T[] {
  return Array.isArray(payload) ? payload : payload.results ?? [];
}
